/**
 * SystemHealthPanel
 *
 * React component that displays live health of all registered game systems.
 * Shown in the DevTools window under the "Health" tab.
 * Polls SystemHealthRegistry every second.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { SystemHealthRegistry, SystemHealthEntry, SystemHealthStatus } from '../../../core/SystemHealthRegistry';

const STATUS_COLORS: Record<SystemHealthStatus, string> = {
  healthy: '#2ecc71',
  degraded: '#f39c12',
  error: '#e74c3c',
  unknown: '#888',
};

const STATUS_ICONS: Record<SystemHealthStatus, string> = {
  healthy: '🟢',
  degraded: '🟡',
  error: '🔴',
  unknown: '⚪',
};

interface ExpandedState {
  [id: string]: boolean;
}

export const SystemHealthPanel: React.FC = () => {
  const [entries, setEntries] = useState<SystemHealthEntry[]>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const refresh = useCallback(() => {
    setEntries(SystemHealthRegistry.getInstance().getAll());
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 1000);
    return () => clearInterval(interval);
  }, [refresh]);

  const summary = SystemHealthRegistry.getInstance().getSummary();

  const toggleExpand = (id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (entries.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚪</div>
        <div>No systems registered yet.</div>
        <div style={{ color: '#555', fontSize: '11px', marginTop: '4px' }}>
          Systems appear here once the game scene is loaded.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Summary Bar */}
      <div style={styles.summaryBar}>
        <span style={{ color: STATUS_COLORS.healthy }}>🟢 {summary.healthy}</span>
        <span style={{ color: STATUS_COLORS.degraded }}>🟡 {summary.degraded}</span>
        <span style={{ color: STATUS_COLORS.error }}>🔴 {summary.error}</span>
        <span style={{ color: '#555' }}>/ {summary.total} systems</span>
      </div>

      {/* System List */}
      <div style={styles.list}>
        {entries.sort((a, b) => {
          // Sort by severity: error > degraded > unknown > healthy
          const order: Record<SystemHealthStatus, number> = { error: 0, degraded: 1, unknown: 2, healthy: 3 };
          return order[a.status] - order[b.status];
        }).map(entry => {
          const msSinceTick = Date.now() - entry.lastTickMs;
          const isExpanded = expanded[entry.id];
          return (
            <div
              key={entry.id}
              style={{ ...styles.systemRow, borderLeftColor: STATUS_COLORS[entry.status] }}
              onClick={() => toggleExpand(entry.id)}
            >
              <div style={styles.rowHeader}>
                <span style={{ color: STATUS_COLORS[entry.status], marginRight: '6px' }}>
                  {STATUS_ICONS[entry.status]}
                </span>
                <span style={styles.systemName}>{entry.id}</span>
                <span style={styles.tickAge}>
                  {msSinceTick < 60000
                    ? `${(msSinceTick / 1000).toFixed(1)}s ago`
                    : 'stale'}
                </span>
                {entry.errorCount > 0 && (
                  <span style={styles.errorBadge}>{entry.errorCount} err</span>
                )}
                <span style={{ color: '#555', fontSize: '10px', marginLeft: 'auto' }}>
                  {isExpanded ? '▲' : '▼'}
                </span>
              </div>

              {isExpanded && (
                <div style={styles.expandedContent}>
                  <div style={{ color: '#888' }}>
                    <strong>Status:</strong>{' '}
                    <span style={{ color: STATUS_COLORS[entry.status] }}>
                      {entry.status.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ color: '#888' }}>
                    <strong>Last tick:</strong> {new Date(entry.lastTickMs).toLocaleTimeString()}
                  </div>
                  {entry.lastError && (
                    <div style={{ color: '#e74c3c', marginTop: '4px' }}>
                      <strong>Last error:</strong> {entry.lastError}
                    </div>
                  )}
                  {entry.lastErrorStack && (
                    <pre style={styles.stackTrace}>{entry.lastErrorStack}</pre>
                  )}
                  {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                    <div style={{ marginTop: '4px', color: '#aaa' }}>
                      <strong>Metadata:</strong>
                      <pre style={styles.metadata}>{JSON.stringify(entry.metadata, null, 2)}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#ccc',
    height: '100%',
    overflowY: 'auto',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '120px',
    color: '#666',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  summaryBar: {
    display: 'flex',
    gap: '12px',
    padding: '6px 8px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '4px',
    fontSize: '12px',
    alignItems: 'center',
    marginBottom: '4px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  systemRow: {
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '4px',
    borderLeft: '3px solid #333',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  systemName: {
    flex: 1,
    fontWeight: 'bold',
    color: '#eee',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tickAge: {
    color: '#666',
    fontSize: '10px',
    marginRight: '6px',
  },
  errorBadge: {
    background: 'rgba(231, 76, 60, 0.3)',
    border: '1px solid #e74c3c',
    borderRadius: '3px',
    padding: '1px 5px',
    color: '#e74c3c',
    fontSize: '10px',
  },
  expandedContent: {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    fontSize: '11px',
    lineHeight: '1.6',
  },
  stackTrace: {
    marginTop: '4px',
    padding: '6px',
    background: 'rgba(0,0,0,0.4)',
    borderRadius: '3px',
    fontSize: '10px',
    color: '#e74c3c',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: '80px',
    overflowY: 'auto',
  },
  metadata: {
    marginTop: '4px',
    padding: '6px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '3px',
    fontSize: '10px',
    color: '#aaa',
    maxHeight: '60px',
    overflowY: 'auto',
  },
};
